import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import { AnimatedCounter, NewsletterSignup } from '../components/common';

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/v1/stats`)
      .then(res => setStats(res.data))
      .catch(err => console.error(err));
  }, []);

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">Global News Intelligence</div>
          <h1 className="hero-title">
            See How The World<br />
            <span className="highlight">Frames The News</span>
          </h1>
          <p className="hero-subtitle">
            The same story. Five regions. Twenty sources. Exposed framing.<br />
            Framecast reveals how media outlets shape narratives differently across the globe.
          </p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={() => navigate('/stories')} data-testid="explore-btn">
              Explore Today's Stories
            </button>
            <button className="btn-secondary" onClick={() => navigate('/methodology')}>
              How It Works
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">
                <AnimatedCounter value={stats?.total_sources || 20} duration={1500} />
              </span>
              <span className="stat-label">News Sources</span>
            </div>
            <div className="stat">
              <span className="stat-number">
                <AnimatedCounter value={5} duration={1000} />
              </span>
              <span className="stat-label">Global Regions</span>
            </div>
            <div className="stat">
              <span className="stat-number">
                <AnimatedCounter value={stats?.total_clusters || 90} duration={2000} suffix="+" />
              </span>
              <span className="stat-label">Stories Tracked</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="section-container">
          <h2 className="section-title">The Problem With News Today</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">🔍</div>
              <h3>Invisible Framing</h3>
              <p>Every news outlet frames stories differently based on their audience, politics, and geography. Most readers never see these differences.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">🌐</div>
              <h3>Filter Bubbles</h3>
              <p>Algorithms show you what you already believe. You're missing how billions of others understand the same events.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">⚖️</div>
              <h3>Bias Overload</h3>
              <p>Every "bias checker" has its own bias. Subjective ratings don't help you think—they tell you what to think.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="section-container">
          <div className="solution-content">
            <div className="solution-text">
              <h2 className="section-title">Our Approach: Transparency, Not Judgment</h2>
              <p className="solution-description">
                Framecast doesn't rate outlets as "trustworthy" or "biased." We simply show you how the same story is covered across different regions—side by side.
              </p>
              <ul className="solution-list">
                <li><strong>No bias scores.</strong> We don't tell you who to trust.</li>
                <li><strong>No AI summaries.</strong> Read the actual headlines.</li>
                <li><strong>No personalization.</strong> Everyone sees the same stories.</li>
                <li><strong>Full transparency.</strong> Every framing signal is inspectable.</li>
              </ul>
            </div>
            <div className="solution-visual">
              <div className="region-preview">
                <div className="region-pill">🇺🇸 United States</div>
                <div className="region-pill">🇪🇺 Europe</div>
                <div className="region-pill">🇮🇳 India</div>
                <div className="region-pill">🌍 Middle East</div>
                <div className="region-pill">🌏 East Asia</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-section">
        <div className="section-container">
          <h2 className="section-title">How Framecast Works</h2>
          <div className="how-grid">
            <div className="how-step">
              <div className="step-number">01</div>
              <h3>Ingest</h3>
              <p>We pull articles from 20+ major news outlets across 5 global regions every 15 minutes.</p>
            </div>
            <div className="how-step">
              <div className="step-number">02</div>
              <h3>Cluster</h3>
              <p>Our algorithms group articles covering the same event or story together.</p>
            </div>
            <div className="how-step">
              <div className="step-number">03</div>
              <h3>Analyze</h3>
              <p>We detect mechanical framing signals—who's quoted, what data is cited, what's emphasized.</p>
            </div>
            <div className="how-step">
              <div className="step-number">04</div>
              <h3>Compare</h3>
              <p>View headlines side-by-side and see how the same story looks from different perspectives.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-container">
          <h2 className="section-title">What You Get</h2>
          <div className="features-grid">
            <div className="feature-card">
              <h3>Daily Story Comparisons</h3>
              <p>See today's biggest stories with coverage from all 5 regions displayed side-by-side.</p>
            </div>
            <div className="feature-card">
              <h3>Framing Tag Detection</h3>
              <p>We flag 9 mechanical signals: government quotes, casualty mentions, expert citations, and more.</p>
            </div>
            <div className="feature-card">
              <h3>Shareable Comparisons</h3>
              <p>Share eye-opening comparisons with friends, colleagues, or on social media.</p>
            </div>
            <div className="feature-card">
              <h3>Daily Digest</h3>
              <p>Pro subscribers get a morning email with the day's most interesting framing differences.</p>
            </div>
            <div className="feature-card">
              <h3>Source Transparency</h3>
              <p>Every source is labeled with its editorial positioning and ownership structure.</p>
            </div>
            <div className="feature-card">
              <h3>Open Methodology</h3>
              <p>Our entire approach is documented. No black boxes, no hidden algorithms.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="enterprise-banner-section">
        <div className="section-container">
          <div className="enterprise-banner" onClick={() => navigate('/enterprise')} data-testid="enterprise-banner">
            <div className="enterprise-banner-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                <path d="M9 22v-4h6v4"></path>
                <path d="M8 6h.01"></path>
                <path d="M16 6h.01"></path>
                <path d="M12 6h.01"></path>
                <path d="M12 10h.01"></path>
                <path d="M12 14h.01"></path>
                <path d="M16 10h.01"></path>
                <path d="M16 14h.01"></path>
                <path d="M8 10h.01"></path>
                <path d="M8 14h.01"></path>
              </svg>
            </div>
            <div className="enterprise-banner-content">
              <div className="enterprise-banner-label">For Teams & Organizations</div>
              <h3>Framecast Enterprise</h3>
              <p>API access, custom topic tracking, team dashboards, and narrative intelligence for investors, comms teams, and researchers.</p>
            </div>
            <div className="enterprise-banner-cta">
              <span className="enterprise-arrow">→</span>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section">
        <div className="section-container">
          <NewsletterSignup variant="landing" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <h2>Ready to See Beyond Your Bubble?</h2>
          <p>Start exploring how global media frames the news differently.</p>
          <div className="cta-buttons">
            <button className="btn-primary large" onClick={() => navigate('/stories')}>
              Explore Stories Free
            </button>
            <button className="btn-secondary large" onClick={() => navigate('/subscribe')}>
              Subscribe for $1/week
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h3>Framecast</h3>
            <p>&ldquo;No bias scores. No trust ratings. Just visibility.&rdquo;</p>
          </div>
          <div className="footer-links-grid">
            <div className="footer-col">
              <h4>Product</h4>
              <Link to="/stories">Today's Stories</Link>
              <Link to="/sources">Our Sources</Link>
              <Link to="/methodology">Methodology</Link>
            </div>
            <div className="footer-col">
              <h4>Subscribe</h4>
              <Link to="/subscribe">Pricing</Link>
              <a href="mailto:support@framecast.news">Support</a>
            </div>
            <div className="footer-col">
              <h4>Enterprise</h4>
              <Link to="/enterprise">For Teams</Link>
              <Link to="/enterprise#api">API Access</Link>
              <Link to="/enterprise#pricing">Enterprise Pricing</Link>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <Link to="/privacy">Privacy Policy</Link>
              <a href="mailto:contact@framecast.news">Contact</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 Framecast News. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
